import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useOutletContext } from 'react-router-dom';
import theme, { tierConfig } from './theme';
import api from '../utils/api';
import { currentYYYYMM } from './overviewMonthUtils';
import DashboardInsightCard from './DashboardInsightCard';

/** Subscription mockup palette (matches design spec) */
const sub = {
  canvas: '#F9F8F3',
  purple: '#5046B3',
  purpleSoft: '#EDE9FE',
  purpleBorder: '#c4b5fd',
  purpleText: '#3730a3',
  green: '#21966B',
  greyMuted: '#A1A1AA',
  barTrack: '#e4e4e7',
  ceiling: '#ef4444',
  /** “Available plans” cards — match subscription mockup */
  planCardBorder: '#E5E7EB',
  planCardBg: '#FFFFFF',
  planTitle: '#111827',
  planDetail: '#6B7280',
  planPrice: '#111827',
  planActiveBorder: '#6366F1',
  planActiveBg: '#F5F3FF',
  planActiveTitle: '#4F46E5',
  planActiveDetail: '#7C73B3',
  planActivePrice: '#4F46E5',
  planComing: '#9CA3AF',
};

const GOLD_TIER_META = { id: 'Gold', hours: 30, perHourINR: 400, monthlyINR: 12000, coming: false };

function parseTierFromPlanName(planName) {
  if (!planName || typeof planName !== 'string') return null;
  const base = planName.replace(/-plan$/i, '').replace(/_/g, ' ').trim();
  if (!base) return null;
  return base.charAt(0).toUpperCase() + base.slice(1).toLowerCase();
}

function pickLatestPayment(paymentHistory) {
  if (!Array.isArray(paymentHistory) || !paymentHistory.length) return null;
  return [...paymentHistory].sort((a, b) => {
    const da = new Date(a.paymentDate || a.createdAt || 0).getTime();
    const db = new Date(b.paymentDate || b.createdAt || 0).getTime();
    return db - da;
  })[0];
}

function formatINR(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n));
}

function formatDateShort(iso) {
  if (!iso || iso === '—') return '—';
  const parsed = parseRenewalDateFlexible(iso);
  if (parsed) return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Parse billing / renewal values from APIs (ISO, DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, Unix s/ms).
 * Prefer this over `new Date(string)` alone — many backends send non-ISO dates.
 */
function parseRenewalDateFlexible(raw) {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s0 = String(raw).trim();
  if (!s0 || s0 === '—') return null;

  const dIsoTry = new Date(s0);
  if (!Number.isNaN(dIsoTry.getTime())) return dIsoTry;

  // DD/MM/YYYY or DD-MM-YYYY (day first — common in IN APIs)
  const m1 = s0.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s|$)/);
  if (m1) {
    const day = Number(m1[1]);
    const month = Number(m1[2]) - 1;
    const year = Number(m1[3]);
    const d = new Date(year, month, day);
    if (!Number.isNaN(d.getTime()) && d.getFullYear() === year && d.getMonth() === month && d.getDate() === day) return d;
  }

  const m2 = s0.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m2) {
    const y = Number(m2[1]);
    const mo = Number(m2[2]) - 1;
    const day = Number(m2[3]);
    const d = new Date(y, mo, day);
    if (!Number.isNaN(d.getTime())) return d;
  }

  // Compact YYYYMMDD
  const m3 = s0.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m3) {
    const y = Number(m3[1]);
    const mo = Number(m3[2]) - 1;
    const day = Number(m3[3]);
    const d = new Date(y, mo, day);
    if (!Number.isNaN(d.getTime())) return d;
  }

  return null;
}

/** Long month name for renewal banner (e.g. 15 April 2026). */
function formatRenewalBannerDate(isoOrDate) {
  if (!isoOrDate || isoOrDate === '—') return '—';
  const parsed =
    isoOrDate instanceof Date && !Number.isNaN(isoOrDate.getTime())
      ? isoOrDate
      : parseRenewalDateFlexible(isoOrDate);
  if (parsed) return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate).trim() || '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function isRenewalDateParsable(s) {
  return parseRenewalDateFlexible(s) != null;
}

/** Banner shows in the last N days before renewal / next subscription (always visible in-window; not dismissible). */
const RENEWAL_REMINDER_WINDOW_DAYS = 120;

/** Single line like "Diamond plan" for the renewal reminder (avoids "plan plan"). */
function buildRenewalBannerPlanPhrase(displayTier, latest, plan, usePlanUsage, pu) {
  const candidates = [usePlanUsage && pu?.planLabel, plan?.planName, plan?.name, latest?.planName].filter(
    (s) => typeof s === 'string' && String(s).trim(),
  );
  const raw = candidates[0] != null ? String(candidates[0]).trim() : '';
  if (raw) {
    let s = raw.replace(/-plan$/i, '').replace(/_/g, ' ').trim();
    const lower = s.toLowerCase();
    if (lower.endsWith(' plan')) return s;
    if (lower.endsWith(' tier')) s = s.replace(/\s+tier$/i, '').trim();
    return `${s} plan`;
  }
  const tier = (displayTier || 'Silver').toString().trim();
  return `${tier} plan`;
}

function daysFromNowTo(iso) {
  if (iso == null || iso === '') return null;
  const end = parseRenewalDateFlexible(iso);
  if (!end) {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const endNaive = new Date(t);
    endNaive.setHours(0, 0, 0, 0);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return Math.ceil((endNaive - start) / 86400000);
  }
  end.setHours(0, 0, 0, 0);
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return Math.ceil((end - start) / 86400000);
}

/** First finite number in argument list (skips null / undefined / NaN). */
function firstFiniteNumber(...candidates) {
  for (const v of candidates) {
    if (v === '' || v === null || v === undefined) continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Trim API date-ish values (null, empty, em dash). */
function trimRenewalCandidate(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s === '—') return null;
  return s;
}

const NEXT_BILLING_FIELD_KEYS = [
  'nextSubscriptionDateISO',
  'next_subscription_date_iso',
  'nextSubscriptionDate',
  'next_subscription_date',
  'nextBilling',
  'next_billing_date',
  'nextBillingDate',
  'nextPaymentDate',
  'next_payment_date',
  'renewalDate',
  'trialEndsAt',
  'trial_ends_at',
  'trialEndDate',
  'trial_end_date',
  'trialEnd',
  'subscriptionEndDate',
  'subscription_end_date',
  'subscriptionEndsAt',
  'subscription_end',
  'currentPeriodEnd',
  'current_period_end',
  'billingPeriodEnd',
  'billing_period_end',
  'periodEnd',
  'period_end',
  'expiresAt',
  'expireAt',
  'endDate',
  'end_date',
  'validUntil',
  'valid_until',
];

/** Next charge / renewal / trial end on one plan-usage row or nested plan blob. */
function extractNextBillingFromObjectDeep(o) {
  if (!o || typeof o !== 'object') return null;
  for (const k of NEXT_BILLING_FIELD_KEYS) {
    const t = trimRenewalCandidate(o[k]);
    if (t) return t;
  }
  const nested = o.plan || o.activePlan || o.subscription || o.currentPlan || o.planDetails || {};
  if (nested && typeof nested === 'object' && nested !== o) {
    for (const k of NEXT_BILLING_FIELD_KEYS) {
      const t = trimRenewalCandidate(nested[k]);
      if (t) return t;
    }
  }
  return null;
}

/** Walk `/user/plan-usage` body (object or array) for any billing date. */
function extractBillingFromPlanUsageState(planUsage) {
  if (planUsage == null) return null;
  if (Array.isArray(planUsage)) {
    for (const row of planUsage) {
      const x = extractNextBillingFromObjectDeep(row);
      if (x) return x;
    }
    return null;
  }
  return extractNextBillingFromObjectDeep(planUsage);
}

function earliestPaymentMonthLabel(paymentHistory) {
  if (!Array.isArray(paymentHistory) || !paymentHistory.length) return null;
  let best = Infinity;
  for (const p of paymentHistory) {
    const t = new Date(p.billing_start || p.paymentDate || p.createdAt || 0).getTime();
    if (!Number.isNaN(t) && t < best) best = t;
  }
  if (!Number.isFinite(best)) return null;
  return new Date(best).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

/** Chart `month` / `label` matches refDate’s calendar month (handles Apr, April, Apr 2026). */
function chartMonthLabelMatchesRefDate(monthLabel, refDate = new Date()) {
  if (monthLabel == null) return false;
  const s = String(monthLabel).trim().toLowerCase();
  if (!s || s === '—') return false;
  const shortM = refDate.toLocaleDateString('en-GB', { month: 'short' }).toLowerCase();
  const longM = refDate.toLocaleDateString('en-GB', { month: 'long' }).toLowerCase();
  const y = String(refDate.getFullYear());
  if (s === shortM || s === longM) return true;
  if (s.startsWith(shortM) && (s.length === shortM.length || !/[a-z]/.test(s.charAt(shortM.length)))) return true;
  if (s.includes(shortM) && (s.includes(y) || s.length <= shortM.length + 2)) return true;
  if (s.includes(longM.slice(0, 3)) && s.includes(y)) return true;
  return false;
}

function sumHoursForCalendarMonth(rows, refDate = new Date()) {
  if (!Array.isArray(rows) || !rows.length) return null;
  let sum = 0;
  let hit = false;
  for (const r of rows) {
    const label = r?.month ?? r?.label ?? r?.period;
    if (!chartMonthLabelMatchesRefDate(label, refDate)) continue;
    hit = true;
    sum += Number(r.hours ?? r.h ?? r.Hours ?? 0) || 0;
  }
  return hit ? sum : null;
}

function sumSessionsForCalendarMonth(rows, refDate = new Date()) {
  if (!Array.isArray(rows) || !rows.length) return null;
  let sum = 0;
  let hit = false;
  for (const r of rows) {
    const label = r?.month ?? r?.label ?? r?.period;
    if (!chartMonthLabelMatchesRefDate(label, refDate)) continue;
    hit = true;
    sum +=
      Number(
        r.sessions ??
          r.sessionCount ??
          r.session_count ??
          r.count ??
          r.totalSessions ??
          r.completedSessions ??
          r.s ??
          0,
      ) || 0;
  }
  return hit ? sum : null;
}

function tierHours(tier) {
  if (!tier) return 36;
  const t = tierConfig[tier]?.hours;
  if (t != null) return t;
  if (tier.toLowerCase() === 'gold') return GOLD_TIER_META.hours;
  return 36;
}

/** Combine multiple `/user/plan-usage` rows (e.g. per robot) into one card. */
function aggregatePlanUsageRows(rows) {
  if (!Array.isArray(rows) || !rows.length) return null;
  const usedSeconds = rows.reduce((s, r) => s + (Number(r.usedSeconds) || 0), 0);
  const remainingSeconds = rows.reduce((s, r) => s + (Number(r.remainingSeconds) || 0), 0);
  const totalHoursPerMonth = rows.reduce((s, r) => s + (Number(r.totalHoursPerMonth) || 0), 0);
  const usedH = usedSeconds / 3600;
  const usagePct = totalHoursPerMonth > 0 ? Math.min(100, (usedH / totalHoursPerMonth) * 100) : null;
  let nextBillingMerged = null;
  for (const r of rows) {
    nextBillingMerged = extractNextBillingFromObjectDeep(r);
    if (nextBillingMerged) break;
  }
  return {
    user: rows[0]?.user,
    robotId: rows.map((r) => r.robotId).filter(Boolean).join(', '),
    totalHoursPerMonth: totalHoursPerMonth || null,
    usedSeconds,
    remainingSeconds,
    usagePercentage: usagePct != null ? String(usagePct.toFixed(2)) : null,
    usedTimeFormatted: null,
    remainingTimeFormatted: null,
    planpaymentStatus: rows.map((r) => r.planpaymentStatus).find(Boolean) || null,
    nextBilling: nextBillingMerged,
  };
}

/** Unwrap `/user/plan-usage` response (`data` may be an object or an array of usage rows). */
function normalizePlanUsageBody(res) {
  const d = res?.data;
  if (!d || typeof d !== 'object') return null;
  if (d.success && d.data != null) {
    if (Array.isArray(d.data)) {
      if (d.data.length === 0) return null;
      if (d.data.length === 1) return d.data[0];
      return aggregatePlanUsageRows(d.data);
    }
    if (typeof d.data === 'object') return d.data;
  }
  if (d.success) {
    const { success: _s, message: _m, error: _e, ...rest } = d;
    if (Object.keys(rest).length > 0) return rest;
  }
  return d;
}

/** Map flexible API field names onto one shape for the Current plan card. */
function pickPlanUsage(pu) {
  if (!pu || typeof pu !== 'object') return null;
  const nested = pu.plan || pu.activePlan || pu.subscription || pu.currentPlan || {};

  const usedFromSeconds =
    pu.usedSeconds != null && pu.usedSeconds !== '' && Number.isFinite(Number(pu.usedSeconds))
      ? Number(pu.usedSeconds) / 3600
      : null;
  const remainingFromSeconds =
    pu.remainingSeconds != null && pu.remainingSeconds !== '' && Number.isFinite(Number(pu.remainingSeconds))
      ? Number(pu.remainingSeconds) / 3600
      : null;

  const includedHours = firstFiniteNumber(
    pu.includedHours,
    pu.included_hours,
    pu.hoursIncluded,
    pu.hours_included,
    pu.totalHours,
    pu.total_hours,
    pu.totalHoursPerMonth,
    pu.total_hours_per_month,
    pu.planHours,
    pu.plan_hours,
    pu.tierHours,
    pu.allocHours,
    pu.allocatedHours,
    nested.includedHours,
    nested.hoursIncluded,
    nested.totalHours,
    nested.hours,
    nested.totalHoursPerMonth,
  );

  const usedHours = firstFiniteNumber(
    pu.usedHours,
    pu.used_hours,
    pu.hoursUsed,
    pu.hours_used,
    pu.consumedHours,
    pu.consumed_hours,
    nested.usedHours,
    nested.hoursUsed,
    usedFromSeconds,
  );

  const remainingHours = firstFiniteNumber(
    pu.remainingHours,
    pu.remaining_hours,
    pu.hoursRemaining,
    pu.hours_remaining,
    nested.remainingHours,
    remainingFromSeconds,
  );

  const monthlyFee = firstFiniteNumber(
    pu.monthlyFee,
    pu.monthly_fee,
    pu.price,
    pu.amount,
    pu.finalPrice,
    nested.monthlyFee,
    nested.price,
  );
  const nextBilling = extractNextBillingFromObjectDeep(pu) || extractNextBillingFromObjectDeep(nested);
  const usagePct = firstFiniteNumber(pu.usagePct, pu.usage_pct, pu.usagePercentage, pu.usage_percentage);

  const usedTimeFormatted =
    typeof pu.usedTimeFormatted === 'string' && pu.usedTimeFormatted.trim() ? pu.usedTimeFormatted.trim() : null;
  const remainingTimeFormatted =
    typeof pu.remainingTimeFormatted === 'string' && pu.remainingTimeFormatted.trim() ? pu.remainingTimeFormatted.trim() : null;

  const planpaymentStatus =
    typeof pu.planpaymentStatus === 'string' && pu.planpaymentStatus.trim() ? pu.planpaymentStatus.trim() : null;

  const planLabel =
    (typeof pu.planName === 'string' && pu.planName.trim()) ||
    (typeof pu.plan_name === 'string' && pu.plan_name.trim()) ||
    (typeof pu.name === 'string' && pu.name.trim() && pu.name !== pu.user) ||
    (typeof pu.label === 'string' && pu.label.trim()) ||
    (typeof nested.name === 'string' && nested.name.trim()) ||
    (typeof nested.planName === 'string' && nested.planName.trim()) ||
    null;

  const hasPlanUsageRow =
    includedHours != null ||
    usedHours != null ||
    remainingHours != null ||
    usedFromSeconds != null ||
    remainingFromSeconds != null ||
    firstFiniteNumber(pu.totalHoursPerMonth) != null ||
    planLabel != null ||
    monthlyFee != null ||
    nextBilling != null ||
    usagePct != null;

  if (!hasPlanUsageRow) return null;

  return {
    planLabel,
    includedHours,
    usedHours,
    remainingHours,
    usedTimeFormatted,
    remainingTimeFormatted,
    monthlyFee,
    nextBilling,
    usagePct,
    planpaymentStatus,
  };
}

/** Semicircle usage gauge (0–100 %) */
function SemicircleGauge({ pct }) {
  const p = Math.max(0, Math.min(100, pct));
  const r = 88;
  const cx = 100;
  const cy = 100;
  const arcLen = Math.PI * r;
  const dash = (p / 100) * arcLen;
  return (
    <Box sx={{ width: 200, mx: 'auto', position: 'relative', height: 108 }}>
      <svg width="200" height="108" viewBox="0 0 200 120" aria-hidden>
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke={sub.barTrack}
          strokeWidth={14}
          strokeLinecap="round"
        />
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke={sub.purple}
          strokeWidth={14}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${arcLen}`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 8,
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        <Typography sx={{ fontSize: 26, fontWeight: 700, color: sub.purpleText, lineHeight: 1.1 }}>
          {p}%
        </Typography>
        <Typography sx={{ fontSize: 12, color: sub.greyMuted, fontWeight: 500 }}>this month</Typography>
      </Box>
    </Box>
  );
}

function StatDivider() {
  return <Box sx={{ borderTop: `1px solid ${theme.colors.borderDefault}`, my: 0 }} />;
}

/**
 * Unwraps AI/cache payloads like Mongo `surface: "subscription"` docs or `{ success, data }` envelopes.
 */
function normalizeUsageForecastInsight(fromRes) {
  const raw = fromRes && typeof fromRes === 'object' && 'data' in fromRes ? fromRes.data : fromRes;
  if (raw == null || typeof raw !== 'object') return null;
  const payload = raw.success !== undefined && raw.data !== undefined ? raw.data : raw;
  if (!payload || typeof payload !== 'object') return null;
  const narrative = typeof payload.narrative === 'string' ? payload.narrative.trim() : '';
  const points = Array.isArray(payload.points) ? payload.points : [];
  const error = typeof payload.error === 'string' ? payload.error.trim() : '';
  if (!error && !narrative && points.length === 0) return null;
  return {
    narrative,
    points,
    error,
    fromCache: payload.fromCache ?? false,
    generatedAt: payload.generatedAt,
    cacheMode: payload.cacheMode,
    source: payload.source,
  };
}

/** seconds -> hours (number), null-safe. */
function secToHours(sec) {
  if (sec == null || sec === '' || !Number.isFinite(Number(sec))) return 0;
  return Number(sec) / 3600;
}

/** "12.5 h" formatting for entitlement / usage rows. */
function formatHoursLabel(h) {
  if (h == null || !Number.isFinite(Number(h))) return '—';
  return `${(Math.round(Number(h) * 10) / 10).toFixed(1)} h`;
}

/** Payment / subscription status -> chip colors + label. */
const STATUS_CHIP = {
  captured: { bg: '#ecfdf5', color: '#047857', label: 'Captured' },
  active: { bg: '#ecfdf5', color: '#047857', label: 'Active' },
  authenticated: { bg: '#eff6ff', color: '#1d4ed8', label: 'Authenticated' },
  authorized: { bg: '#eff6ff', color: '#1d4ed8', label: 'Authorized' },
  trial: { bg: '#f5f3ff', color: '#6d28d9', label: 'Trial' },
  pending: { bg: '#fffbeb', color: '#b45309', label: 'Pending' },
  created: { bg: '#f8fafc', color: '#475569', label: 'Created' },
  refunded: { bg: '#fef2f2', color: '#b91c1c', label: 'Refunded' },
  failed: { bg: '#fef2f2', color: '#b91c1c', label: 'Failed' },
  expired: { bg: '#f1f5f9', color: '#64748b', label: 'Expired' },
};

function statusChipStyle(status) {
  const key = String(status || '').trim().toLowerCase();
  return (
    STATUS_CHIP[key] || {
      bg: '#f1f5f9',
      color: '#475569',
      label: status ? String(status) : '—',
    }
  );
}

/** Small pill component for statuses. */
function StatusChip({ status, size = 'sm' }) {
  const s = statusChipStyle(status);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: size === 'sm' ? 1 : 1.25,
        py: size === 'sm' ? 0.25 : 0.5,
        borderRadius: 999,
        bgcolor: s.bg,
        color: s.color,
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'capitalize',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </Box>
  );
}

/** Find the catalog entry (from `/client/subscription` availablePlans) matching the active plan. */
function matchCatalogForActive(availablePlansApi, activeRich) {
  if (!Array.isArray(availablePlansApi) || !availablePlansApi.length || !activeRich) return null;
  return (
    availablePlansApi.find(
      (p) =>
        (activeRich.planId && p.id && String(p.id) === String(activeRich.planId)) ||
        (activeRich.planName &&
          p.planName &&
          String(p.planName).toLowerCase() === String(activeRich.planName).toLowerCase()),
    ) || null
  );
}

/** Card shell used across the revamped sections. */
function SectionCard({ title, subtitle, action, children, sx, id }) {
  return (
    <Paper
      id={id}
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '12px',
        bgcolor: '#fff',
        border: `1px solid ${theme.colors.borderDefault}`,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        height: '100%',
        scrollMarginTop: 96,
        ...sx,
      }}
    >
      {(title || action) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: subtitle ? 0.25 : 2 }}>
          <Box>
            {title ? (
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary, letterSpacing: '-0.01em' }}>
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography sx={{ fontSize: 13, color: sub.greyMuted, fontWeight: 500, mt: 0.25 }}>{subtitle}</Typography>
            ) : null}
          </Box>
          {action || null}
        </Box>
      )}
      {subtitle ? <Box sx={{ mb: 2 }} /> : null}
      {children}
    </Paper>
  );
}

/** Stacked horizontal entitlement bar: main + add-on + carry-forward, with the used portion overlaid. */
function EntitlementBar({ mainHours, addonHours, bonusHours, usedHours }) {
  const total = (mainHours || 0) + (addonHours || 0) + (bonusHours || 0);
  const safeTotal = total > 0 ? total : 1;
  const seg = (h, color, label) =>
    h > 0 ? (
      <Box
        key={label}
        title={`${label}: ${formatHoursLabel(h)}`}
        sx={{ width: `${(h / safeTotal) * 100}%`, bgcolor: color, height: '100%' }}
      />
    ) : null;
  const usedPct = Math.min(100, Math.round((Math.max(0, usedHours) / safeTotal) * 100));
  return (
    <Box>
      <Box sx={{ position: 'relative', mb: 1.25 }}>
        <Box sx={{ display: 'flex', height: 14, borderRadius: 999, overflow: 'hidden', bgcolor: sub.barTrack }}>
          {seg(mainHours, sub.purple, 'Included')}
          {seg(addonHours, '#0d9488', 'Add-on')}
          {seg(bonusHours, '#d97706', 'Carry-forward')}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: -3,
            left: `calc(${usedPct}% - 1px)`,
            height: 20,
            width: 2,
            bgcolor: theme.colors.textPrimary,
            borderRadius: 1,
          }}
          title={`Used ${formatHoursLabel(usedHours)} (${usedPct}%)`}
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {[
          { label: 'Included', h: mainHours, color: sub.purple },
          { label: 'Add-on', h: addonHours, color: '#0d9488' },
          { label: 'Carry-forward', h: bonusHours, color: '#d97706' },
        ]
          .filter((x) => (x.h || 0) > 0 || x.label === 'Included')
          .map((x) => (
            <Box key={x.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: x.color }} />
              <Typography sx={{ fontSize: 12, color: sub.greyMuted, fontWeight: 500 }}>
                {x.label} <Box component="span" sx={{ color: theme.colors.textPrimary, fontWeight: 700 }}>{formatHoursLabel(x.h || 0)}</Box>
              </Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
}

/** Billing / invoice ledger built from paymentHistory[]. */
function BillingLedger({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: sub.greyMuted, fontStyle: 'italic', py: 2 }}>
        No payments recorded yet for this account.
      </Typography>
    );
  }
  const cols = '120px 1fr 130px 110px 1fr';
  const cellSx = { fontSize: 13, color: theme.colors.textPrimary, minWidth: 0 };
  const headSx = { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: sub.greyMuted, textTransform: 'uppercase' };
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 640 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 1.5, px: 1, pb: 1 }}>
          <Typography sx={headSx}>Date</Typography>
          <Typography sx={headSx}>Plan</Typography>
          <Typography sx={headSx}>Amount</Typography>
          <Typography sx={headSx}>Status</Typography>
          <Typography sx={headSx}>Billing period</Typography>
        </Box>
        {rows.map((p, i) => {
          const discounted = p.originalPrice != null && p.discount > 0 && Number(p.originalPrice) !== Number(p.finalPrice);
          const periodStart = p.billing_start || p.trial_start;
          const periodEnd = p.billing_end || p.next_payment_date || p.trial_end;
          const period =
            periodStart || periodEnd
              ? `${periodStart ? formatDateShort(periodStart) : '—'} → ${periodEnd ? formatDateShort(periodEnd) : '—'}`
              : '—';
          return (
            <Box
              key={p.razorpay_payment_id || p.razorpay_order_id || `${p.paymentDate}-${i}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: cols,
                gap: 1.5,
                px: 1,
                py: 1.25,
                alignItems: 'center',
                borderTop: `1px solid ${theme.colors.borderDefault}`,
              }}
            >
              <Typography sx={cellSx}>{formatDateShort(p.paymentDate || p.createdAt)}</Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...cellSx, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.planName || '—'}
                </Typography>
                {p.invoice_number ? (
                  <Typography sx={{ fontSize: 11, color: sub.greyMuted }}>{p.invoice_number}</Typography>
                ) : p.payment_method ? (
                  <Typography sx={{ fontSize: 11, color: sub.greyMuted, textTransform: 'capitalize' }}>{p.payment_method}</Typography>
                ) : null}
              </Box>
              <Box>
                <Typography sx={{ ...cellSx, fontWeight: 700 }}>{formatINR(p.finalPrice)}</Typography>
                {discounted ? (
                  <Typography sx={{ fontSize: 11, color: sub.greyMuted, textDecoration: 'line-through' }}>
                    {formatINR(p.originalPrice)}
                  </Typography>
                ) : null}
              </Box>
              <Box>
                <StatusChip status={p.status} />
              </Box>
              <Typography sx={{ ...cellSx, color: sub.greyMuted, fontSize: 12 }}>{period}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** Subscription lifecycle timeline: start -> trial -> paid months -> next due -> expiry. */
function LifecycleTimeline({ lifecycle }) {
  if (!lifecycle) {
    return (
      <Typography sx={{ fontSize: 13, color: sub.greyMuted, fontStyle: 'italic', py: 2 }}>
        No active subscription cycle to display.
      </Typography>
    );
  }
  const now = Date.now();
  const upcomingDue = (lifecycle.dueDates || [])
    .map((d) => ({ raw: d, t: new Date(d).getTime() }))
    .filter((x) => !Number.isNaN(x.t))
    .sort((a, b) => a.t - b.t);
  const nextDue = upcomingDue.find((x) => x.t >= now) || upcomingDue[upcomingDue.length - 1] || null;

  const paid = lifecycle.paidMonthsCompleted;
  const commit = lifecycle.autoPayMonths;
  const steps = [
    { label: 'Started', date: lifecycle.startDate, done: true },
    {
      label: lifecycle.freeMonths ? `Trial / ${lifecycle.freeMonths} free mo` : 'Trial',
      date: (lifecycle.trialDates || [])[0] || lifecycle.statusTimestamps?.trial || null,
      done: true,
    },
    {
      label:
        paid != null && commit != null
          ? `Paid ${paid}/${commit} mo`
          : paid != null
            ? `Paid ${paid} mo`
            : 'Billing',
      date: lifecycle.statusTimestamps?.active || null,
      done: paid != null && paid > 0,
    },
    { label: 'Next charge', date: nextDue?.raw || lifecycle.nextPaymentDate, done: false, highlight: true },
    { label: 'Expires', date: lifecycle.planExpiryDate, done: false },
  ].filter((s) => s.date || s.label);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, alignItems: 'stretch' }}>
      {steps.map((s, i) => (
        <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', flex: '1 1 140px', minWidth: 130 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: s.highlight ? sub.purple : s.done ? sub.green : sub.barTrack,
                  border: s.highlight ? `3px solid ${sub.purpleSoft}` : 'none',
                }}
              />
              {i < steps.length - 1 ? (
                <Box sx={{ flex: 1, height: 2, bgcolor: sub.barTrack, mr: 0.5 }} />
              ) : null}
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: theme.colors.textPrimary, mt: 1 }}>{s.label}</Typography>
            <Typography sx={{ fontSize: 12, color: sub.greyMuted }}>{s.date ? formatDateShort(s.date) : '—'}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function SubscriptionPage() {
  const { clientEmail } = useOutletContext();
  const [data, setData] = React.useState(null);
  const [overviewData, setOverviewData] = React.useState(null);
  const [planUsage, setPlanUsage] = React.useState(null);
  const [usageForecastInsight, setUsageForecastInsight] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const refetchUsageForecast = React.useCallback(async () => {
    const email = clientEmail || localStorage.getItem('email');
    if (!email) return;
    try {
      const subRes = await api.get(`/client/subscription?email=${encodeURIComponent(email)}`);
      if (subRes.data?.success) {
        const fromSub =
          normalizeUsageForecastInsight({ data: subRes.data?.usageForecast }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.usageForecastCache }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.subscriptionInsights }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.insightsFromView });
        setUsageForecastInsight(fromSub || null);
      }
    } catch (e) {
      console.error('Usage forecast refetch error:', e);
    }
  }, [clientEmail]);

  React.useEffect(() => {
    const load = async () => {
      const email = clientEmail || localStorage.getItem('email');
      if (!email) {
        setLoading(false);
        return;
      }
      try {
        const [subRes, ovRes, puRes, insightRes] = await Promise.all([
          api.get(`/client/subscription?email=${encodeURIComponent(email)}`).catch(() => ({ data: {} })),
          api.get(`/client/overview?email=${encodeURIComponent(email)}`).catch(() => ({ data: {} })),
          api.get('/user/plan-usage', { params: { email } }).catch(() => ({ data: null })),
          api.get('/client/insights', { params: { email, surface: 'subscription' } }).catch(() => ({ data: null })),
        ]);
        if (subRes.data?.success) setData(subRes.data);
        if (ovRes.data?.success) setOverviewData(ovRes.data);
        const puBody = normalizePlanUsageBody(puRes);
        if (puBody) setPlanUsage(puBody);

        const fromDedicated = normalizeUsageForecastInsight(insightRes);
        const fromSub =
          normalizeUsageForecastInsight({ data: subRes.data?.usageForecast }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.usageForecastCache }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.subscriptionInsights }) ||
          normalizeUsageForecastInsight({ data: subRes.data?.insightsFromView });
        setUsageForecastInsight(fromDedicated || fromSub || null);
      } catch (e) {
        console.error('Subscription / overview / plan-usage fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientEmail]);

  const merged = useMemo(() => {
    const plan = data?.plan || {};
    const usd = data?.userSubscriptionDetails ?? {};
    const paymentHistory = usd.paymentHistory || [];
    const latest = pickLatestPayment(paymentHistory);
    const fromPaymentTier = parseTierFromPlanName(latest?.planName);
    const displayTier =
      plan.monthlyFee != null && plan.renewalDate && plan.renewalDate !== '—'
        ? plan.tier || fromPaymentTier
        : fromPaymentTier || plan.tier || 'Silver';

    const monthlyFee =
      plan.monthlyFee != null ? plan.monthlyFee : latest?.finalPrice ?? latest?.amount ?? null;

    const trimDate = trimRenewalCandidate;

    /** Primary renewal / next-charge instant from subscription + payment rows (plan-usage merged later). */
    let renewalIso =
      trimDate(plan.renewalDate) ||
      trimDate(plan.nextSubscriptionDateISO) ||
      trimDate(plan.nextSubscriptionDate) ||
      trimDate(latest?.next_payment_date) ||
      trimDate(latest?.billing_end) ||
      trimDate(plan.nextBilling) ||
      trimDate(plan.nextBillingDate) ||
      trimDate(plan.next_billing_date) ||
      trimDate(plan.nextRenewal) ||
      trimDate(plan.currentPeriodEnd) ||
      trimDate(plan.current_period_end) ||
      trimDate(plan.billingPeriodEnd) ||
      trimDate(plan.billing_period_end) ||
      trimDate(data?.nextRenewal) ||
      trimDate(usd.nextRenewal) ||
      trimDate(usd.nextBilling) ||
      trimDate(usd.next_billing_date) ||
      trimDate(usd.nextBillingDate) ||
      trimDate(usd.trialEndDate) ||
      trimDate(usd.trial_ends_at) ||
      trimDate(usd.trialEndsAt) ||
      trimDate(usd.subscriptionEndDate) ||
      trimDate(usd.subscription_end_date) ||
      trimDate(usd.currentPeriodEnd) ||
      trimDate(usd.current_period_end) ||
      trimDate(usd.nextSubscriptionDateISO) ||
      trimDate(usd.nextSubscriptionDate) ||
      trimDate(extractBillingFromPlanUsageState(planUsage)) ||
      null;

    const puPick = pickPlanUsage(planUsage);
    const usePlanUsage = Boolean(
      puPick &&
        [
          puPick.planLabel,
          puPick.includedHours,
          puPick.usedHours,
          puPick.remainingHours,
          puPick.monthlyFee,
          puPick.nextBilling,
          puPick.usagePct,
          puPick.usedTimeFormatted,
          puPick.remainingTimeFormatted,
          puPick.planpaymentStatus,
        ].some((v) => v != null && v !== ''),
    );
    const pu = puPick || {};

    /** `/user/plan-usage` often carries `nextBilling` when `/client/subscription` omits `plan.renewalDate` (e.g. trials, add-ons). */
    if (usePlanUsage && trimDate(pu.nextBilling) && (!renewalIso || !isRenewalDateParsable(renewalIso))) {
      renewalIso = trimDate(pu.nextBilling);
    }

    /** Single `Date` for labels + day count (handles DD/MM/YYYY, Unix, etc.). */
    const renewalDateResolved =
      parseRenewalDateFlexible(renewalIso) ||
      parseRenewalDateFlexible(trimDate(plan.nextSubscriptionDateISO)) ||
      parseRenewalDateFlexible(trimDate(plan.nextSubscriptionDate)) ||
      parseRenewalDateFlexible(trimDate(extractBillingFromPlanUsageState(planUsage))) ||
      (usePlanUsage ? parseRenewalDateFlexible(trimDate(pu.nextBilling)) : null) ||
      parseRenewalDateFlexible(trimDate(plan.renewalDate)) ||
      null;

    let daysToRenewal =
      plan.daysToRenewal != null && plan.daysToRenewal !== '' && Number.isFinite(Number(plan.daysToRenewal))
        ? Number(plan.daysToRenewal)
        : plan.daysToNextSubscription != null &&
            plan.daysToNextSubscription !== '' &&
            Number.isFinite(Number(plan.daysToNextSubscription))
          ? Number(plan.daysToNextSubscription)
          : null;
    if (daysToRenewal == null) {
      daysToRenewal = renewalDateResolved
        ? daysFromNowTo(renewalDateResolved)
        : renewalIso
          ? daysFromNowTo(renewalIso)
          : null;
    }

    const renewalLabel =
      renewalDateResolved != null
        ? renewalDateResolved.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : formatDateShort(renewalIso) || trimDate(plan.renewalDate) || '—';

    const renewalBannerDateLabel =
      renewalDateResolved != null
        ? formatRenewalBannerDate(renewalDateResolved)
        : renewalLabel !== '—'
          ? renewalLabel
          : '—';

    const renewalPlanNameForBanner = buildRenewalBannerPlanPhrase(displayTier, latest, plan, usePlanUsage, pu);

    const currentPlanTitle =
      usePlanUsage && pu.planLabel
        ? pu.planLabel.trim().toLowerCase().endsWith('tier')
          ? pu.planLabel.trim()
          : `${pu.planLabel.trim()} tier`
        : `${displayTier} tier`;

    const monthlyFeeDisplay = usePlanUsage && pu.monthlyFee != null ? pu.monthlyFee : monthlyFee;

    const tierCeiling =
      usePlanUsage && pu.includedHours != null && pu.includedHours > 0 ? pu.includedHours : tierHours(displayTier);

    const hoursUsed =
      usePlanUsage && pu.usedHours != null
        ? Math.max(0, Number(pu.usedHours))
        : plan.hoursUsed != null
          ? Number(plan.hoursUsed)
          : 0;

    const remainingH =
      usePlanUsage && pu.remainingHours != null
        ? Math.max(0, Number(pu.remainingHours))
        : Math.max(0, tierCeiling - hoursUsed);

    const usagePct =
      usePlanUsage && pu.usagePct != null
        ? Math.round(Math.min(100, Math.max(0, Number(pu.usagePct))))
        : tierCeiling > 0
          ? Math.round(Math.min(100, (hoursUsed / tierCeiling) * 100))
          : 0;

    const planCardNextBilling =
      usePlanUsage && pu.nextBilling != null && String(pu.nextBilling).trim() && String(pu.nextBilling) !== '—'
        ? formatDateShort(pu.nextBilling)
        : renewalDateResolved != null
          ? renewalDateResolved.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : renewalLabel;

    const usedHoursDisplay = usePlanUsage && pu.usedTimeFormatted ? pu.usedTimeFormatted : `${hoursUsed.toFixed(1)} h`;
    const remainingHoursDisplay =
      usePlanUsage && pu.remainingTimeFormatted ? pu.remainingTimeFormatted : `${remainingH.toFixed(1)} h`;

    const usageRaw = Array.isArray(data?.usageChart) ? [...data.usageChart] : [];
    const usageActual = usageRaw.map((row) => ({
      month: row.month ?? row.label ?? '—',
      hours: Number(row.hours ?? row.h ?? 0) || 0,
    }));
    const hasRealUsage = usageActual.length > 0 && usageActual.some((r) => r.hours > 0);
    // Show real data only; never fall back to fabricated placeholder bars.
    const usageChartIsPlaceholder = !hasRealUsage;
    const usageChart = usageActual;

    const hoursListForStats = usageActual.map((r) => r.hours);
    const avgH = hoursListForStats.length ? hoursListForStats.reduce((a, b) => a + b, 0) / hoursListForStats.length : 0;
    let peakH = 0;
    let peakMonth = '';
    usageActual.forEach((r) => {
      if (r.hours > peakH) {
        peakH = r.hours;
        peakMonth = r.month;
      }
    });

    const lifetime = data?.lifetimeSummary || data?.lifetime || {};
    const ov = overviewData || {};
    const k = ov.kpis || {};
    const hoursChart = Array.isArray(ov.hoursChart) ? ov.hoursChart : [];
    const hoursFromChart = hoursChart.reduce((sum, row) => sum + (Number(row.hours ?? row.Hours ?? 0) || 0), 0);
    const hoursFromSubscriptionChart = usageActual.reduce((sum, row) => sum + (Number(row.hours) || 0), 0);

    const lifetimeHoursVal =
      firstFiniteNumber(
        lifetime.lifetimeHours,
        lifetime.lifetime_uvc_hours,
        lifetime.totalUvcHours,
        data?.lifetimeHours,
        data?.totalUvcHours,
        k.totalUvcHours,
        k.lifetimeUvcHours,
        k.lifetimeHours,
        k.hoursTotal,
        k.hoursAllTime,
        k.hoursLifetime,
      ) ??
      (hoursFromSubscriptionChart > 0 ? hoursFromSubscriptionChart : null) ??
      (hoursFromChart > 0 ? hoursFromChart : null);

    const totalSessionsVal = firstFiniteNumber(
      data?.lifetimeSessions,
      data?.userTotalSessions,
      usd.lifetimeSessions,
      usd.userTotalSessions,
      usd.totalSessions,
      plan.lifetimeSessions,
      plan.userTotalSessions,
      plan.totalSessions,
      lifetime.lifetimeSessions,
      lifetime.totalSessions,
      lifetime.total_sessions,
      data?.totalSessions,
      k.userTotalSessions,
      k.totalSessions,
      k.lifetimeSessions,
      k.sessionCount,
      k.sessionsTotal,
      k.sessions,
    );

    const roomCountVal = firstFiniteNumber(lifetime.roomCount, lifetime.rooms_covered, k.roomsCovered, k.rooms, k.uniqueRooms);

    const earliestPaid = earliestPaymentMonthLabel(paymentHistory);
    const lifetimeHoursSub =
      lifetime.sinceLabel ||
      lifetime.since ||
      (earliestPaid ? `since ${earliestPaid}` : null) ||
      (hoursFromSubscriptionChart > 0 ? 'Total from subscription usage chart' : null) ||
      (hoursFromChart > 0 ? 'Total from UVC hours chart (overview)' : null) ||
      '—';

    const totalSessionsSub =
      lifetime.roomsLabel ||
      (roomCountVal != null && roomCountVal >= 0 ? `across ${roomCountVal} room${roomCountVal === 1 ? '' : 's'}` : null) ||
      (totalSessionsVal != null && k.sessions != null && Number(totalSessionsVal) === Number(k.sessions) ? 'All time · dashboard' : null) ||
      'All time · this account';

    const sessionChartRaw = Array.isArray(data?.sessionsChart)
      ? data.sessionsChart
      : Array.isArray(ov.sessionsChart)
        ? ov.sessionsChart
        : [];

    const refNow = new Date();
    const currentMonthCaption = refNow.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    let currentMonthHoursVal = firstFiniteNumber(
      data?.currentMonthHours,
      data?.hoursThisMonth,
      data?.monthHours,
      lifetime.currentMonthHours,
      lifetime.hoursThisMonth,
      k.currentMonthHours,
      k.hoursThisMonth,
      k.monthHours,
      k.monthlyUvcHours,
    );
    if (currentMonthHoursVal == null) {
      currentMonthHoursVal = sumHoursForCalendarMonth(usageActual, refNow);
    }
    if (currentMonthHoursVal == null) {
      currentMonthHoursVal = sumHoursForCalendarMonth(
        hoursChart.map((row) => ({
          month: row.month ?? row.label ?? row.period,
          hours: row.hours ?? row.Hours,
        })),
        refNow,
      );
    }
    if (currentMonthHoursVal == null && usePlanUsage && hoursUsed != null) {
      currentMonthHoursVal = Number(hoursUsed) || 0;
    }

    let currentMonthSessionsVal = firstFiniteNumber(
      data?.currentMonthSessions,
      plan.currentMonthSessions,
      plan.sessionsThisMonth,
      plan.monthSessions,
      usd.currentMonthSessions,
      usd.sessionsThisMonth,
      usd.monthSessions,
      data?.sessionsThisMonth,
      lifetime.currentMonthSessions,
      k.currentMonthSessions,
      k.sessionsThisMonth,
      k.monthSessions,
      k.sessionsThisMonthCount,
    );
    if (currentMonthSessionsVal == null) {
      currentMonthSessionsVal = sumSessionsForCalendarMonth(sessionChartRaw, refNow);
    }

    const currentMonthHoursSub =
      usePlanUsage &&
      hoursUsed != null &&
      currentMonthHoursVal != null &&
      sumHoursForCalendarMonth(usageActual, refNow) == null &&
      sumHoursForCalendarMonth(
        hoursChart.map((row) => ({
          month: row.month ?? row.label ?? row.period,
          hours: row.hours ?? row.Hours,
        })),
        refNow,
      ) == null &&
      Number(currentMonthHoursVal) === Number(hoursUsed)
        ? `${currentMonthCaption} · from active plan usage`
        : currentMonthCaption;

    const currentMonthSessionsSub = currentMonthCaption;

    // ---- Rich subscription detail (serialized plans + billing summary + ledger) ----
    const richPlans = Array.isArray(usd.plans) ? usd.plans : [];
    const activeRich =
      richPlans.find((p) => p.planpaymentStatus === 'active' || p.planpaymentStatus === 'trial') ||
      usd.activeSubscription ||
      richPlans[0] ||
      null;
    const billing = data?.billingSummary || {};
    const availablePlansApi = Array.isArray(data?.availablePlans) ? data.availablePlans : [];
    const matchedCatalog = matchCatalogForActive(availablePlansApi, activeRich);

    const paymentRows = [...paymentHistory].sort((a, b) => {
      const da = new Date(a.paymentDate || a.createdAt || 0).getTime();
      const db = new Date(b.paymentDate || b.createdAt || 0).getTime();
      return db - da;
    });

    // Entitlement breakdown: main allowance + purchased add-on + carry-forward bonus.
    const mainHours =
      firstFiniteNumber(activeRich?.mainHoursMonthly, activeRich?.totalHoursPerMonth) ?? tierCeiling;
    const addonHours = secToHours(activeRich?.addonPurchasedSeconds);
    const bonusHours = firstFiniteNumber(activeRich?.cycleEndBonusHours) ?? 0;
    const totalEntitlement = (Number(mainHours) || 0) + addonHours + bonusHours;
    const entitlementUsed = hoursUsed;
    const entitlementRemaining =
      activeRich?.remainingSeconds != null
        ? secToHours(activeRich.remainingSeconds)
        : Math.max(0, totalEntitlement - entitlementUsed);

    const sessionsAllowance = firstFiniteNumber(
      matchedCatalog?.benefits?.sessionsPerMonth,
      matchedCatalog?.benefits?.sessions_per_month,
    );
    const currentMonthSessions = firstFiniteNumber(data?.currentMonthSessions) ?? null;
    const lifetimeSessions = firstFiniteNumber(data?.lifetimeSessions) ?? null;

    const heroStatus =
      activeRich?.subscriptionStatus ||
      activeRich?.planpaymentStatus ||
      (plan.status && plan.status !== '—' ? plan.status : null) ||
      null;
    const memberSince = trimDate(billing.firstPaymentDate);

    const lifecycle = activeRich
      ? {
          startDate: activeRich.startDate || activeRich.planPurchaseDate || null,
          trialDates: Array.isArray(activeRich.trialDates) ? activeRich.trialDates : [],
          dueDates: Array.isArray(activeRich.dueDates) ? activeRich.dueDates : [],
          statusTimestamps: activeRich.paymentStatusTimestamps || {},
          paidMonthsCompleted: activeRich.paidMonthsCompleted ?? null,
          freeMonths: activeRich.freeMonths ?? null,
          autoPayMonths: activeRich.autoPayMonths ?? null,
          planExpiryDate: activeRich.planExpiryDate || null,
          nextPaymentDate: activeRich.nextPaymentDate || null,
          planName: activeRich.planName || displayTier,
        }
      : null;

    return {
      plan,
      displayTier,
      monthlyFee,
      monthlyFeeDisplay,
      richPlans,
      activeRich,
      billing,
      paymentRows,
      mainHours: Number(mainHours) || 0,
      addonHours,
      bonusHours,
      totalEntitlement,
      entitlementUsed,
      entitlementRemaining,
      sessionsAllowance,
      currentMonthSessions,
      lifetimeSessions,
      heroStatus,
      memberSince,
      lifecycle,
      renewalLabel,
      renewalBannerDateLabel,
      renewalPlanNameForBanner,
      planCardNextBilling,
      currentPlanTitle,
      usedHoursDisplay,
      remainingHoursDisplay,
      planPaymentStatus: usePlanUsage ? pu.planpaymentStatus : null,
      daysToRenewal,
      tierCeiling,
      hoursUsed,
      usagePct,
      remainingH,
      usageChart,
      usageChartIsPlaceholder,
      avgH,
      peakH,
      peakMonth,
      lifetimeHoursVal,
      lifetimeHoursSub,
      totalSessionsVal,
      totalSessionsSub,
      currentMonthHoursVal,
      currentMonthHoursSub,
      currentMonthSessionsVal,
      currentMonthSessionsSub,
    };
  }, [data, overviewData, planUsage]);

  const showRenewalBanner = useMemo(
    () =>
      merged.daysToRenewal != null &&
      merged.daysToRenewal >= 0 &&
      merged.daysToRenewal <= RENEWAL_REMINDER_WINDOW_DAYS &&
      merged.renewalBannerDateLabel != null &&
      merged.renewalBannerDateLabel !== '—',
    [merged.daysToRenewal, merged.renewalBannerDateLabel],
  );

  const usageForecastSource = React.useMemo(() => {
    const fc = usageForecastInsight;
    const hasPoints = Array.isArray(fc?.points) && fc.points.length > 0;
    const hasNarrative = typeof fc?.narrative === 'string' && fc.narrative.trim().length > 0;
    if (fc?.source === 'ai' || fc?.source === 'template') return fc.source;
    return hasPoints || hasNarrative ? 'ai' : 'template';
  }, [usageForecastInsight]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: sub.purple }} />
      </Box>
    );
  }

  const purchaseTypeNorm = (data?.userSubscriptionDetails?.purchaseType ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  if (data?.success && purchaseTypeNorm === 'purchaseuser') {
    return null;
  }

  const {
    monthlyFeeDisplay,
    renewalBannerDateLabel,
    renewalPlanNameForBanner,
    planCardNextBilling,
    currentPlanTitle,
    usedHoursDisplay,
    remainingHoursDisplay,
    daysToRenewal,
    tierCeiling,
    usagePct,
    usageChart,
    avgH,
    peakH,
    peakMonth,
    usageChartIsPlaceholder,
    lifetimeHoursVal,
    lifetimeHoursSub,
    totalSessionsVal,
    totalSessionsSub,
    currentMonthHoursVal,
    currentMonthHoursSub,
    currentMonthSessionsVal,
    currentMonthSessionsSub,
    billing,
    paymentRows,
    mainHours,
    addonHours,
    bonusHours,
    totalEntitlement,
    entitlementUsed,
    entitlementRemaining,
    sessionsAllowance,
    currentMonthSessions,
    heroStatus,
    memberSince,
    lifecycle,
  } = merged;

  const chartYMax = Math.max(40, Math.ceil(tierCeiling / 10) * 10);
  const subEmail = clientEmail || (typeof localStorage !== 'undefined' ? localStorage.getItem('email') : '') || '';

  return (
    <Box sx={{ bgcolor: sub.canvas, borderRadius: `${theme.radius.md}px`, mx: { xs: -2, md: -3 }, mt: -3, mb: -3, px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Renewal reminder */}
      {showRenewalBanner && (
        <Paper
          elevation={0}
          sx={{
            p: 2.25,
            mb: 3,
            borderRadius: '12px',
            bgcolor: sub.purple,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            boxShadow: '0 4px 14px rgba(80, 70, 179, 0.25)',
          }}
        >
          <Typography
            sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.78)', mb: 0.75 }}
          >
            RENEWAL REMINDER
          </Typography>
          <Typography sx={{ fontSize: 15, color: '#fff', fontWeight: 500, lineHeight: 1.45 }}>
            Your {renewalPlanNameForBanner} renews on {renewalBannerDateLabel}
            {daysToRenewal != null && daysToRenewal >= 0
              ? ` · ${daysToRenewal} day${daysToRenewal === 1 ? '' : 's'} away`
              : ''}
          </Typography>
        </Paper>
      )}

      {/* Status hero strip */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 2.5,
          borderRadius: '12px',
          bgcolor: '#fff',
          border: `1px solid ${theme.colors.borderDefault}`,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: theme.colors.textPrimary }}>{currentPlanTitle}</Typography>
          {heroStatus ? <StatusChip status={heroStatus} size="md" /> : null}
        </Box>
        <Grid container spacing={2}>
          {[
            {
              label: 'MONTHLY FEE',
              value: monthlyFeeDisplay != null ? formatINR(monthlyFeeDisplay) : '—',
              sub: 'Recurring · per month',
            },
            {
              label: 'LIFETIME VALUE',
              value: billing?.lifetimeValue != null ? formatINR(billing.lifetimeValue) : '—',
              sub:
                billing?.totalRefunded > 0
                  ? `${formatINR(billing.totalRefunded)} refunded`
                  : `${billing?.capturedCount ?? 0} captured payment${(billing?.capturedCount ?? 0) === 1 ? '' : 's'}`,
            },
            {
              label: 'NEXT CHARGE',
              value: planCardNextBilling || '—',
              sub:
                daysToRenewal != null && daysToRenewal >= 0
                  ? `${daysToRenewal} day${daysToRenewal === 1 ? '' : 's'} away`
                  : billing?.nextChargeAmount != null
                    ? formatINR(billing.nextChargeAmount)
                    : '—',
            },
            {
              label: 'CLIENT SINCE',
              value: memberSince ? formatDateShort(memberSince) : '—',
              sub:
                billing?.paymentCount != null ? `${billing.paymentCount} payment${billing.paymentCount === 1 ? '' : 's'} on file` : '—',
            },
          ].map((m) => (
            <Grid item xs={6} md={3} key={m.label}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: sub.greyMuted, mb: 0.75 }}>
                {m.label}
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.colors.textPrimary, lineHeight: 1.15 }}>
                {m.value}
              </Typography>
              <Typography sx={{ fontSize: 12, color: sub.greyMuted, mt: 0.5, fontWeight: 500 }}>{m.sub}</Typography>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Top row: entitlement & usage + utilisation chart */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} lg={5}>
          <SectionCard title="Entitlement & usage" sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: 13, color: sub.greyMuted, mb: 2 }}>
              {totalEntitlement > 0 ? `${formatHoursLabel(totalEntitlement)} total this cycle` : 'Allowance not set'}
            </Typography>

            <SemicircleGauge pct={usagePct} />

            <Box sx={{ mt: 2, mb: 1.5 }}>
              <EntitlementBar
                mainHours={mainHours}
                addonHours={addonHours}
                bonusHours={bonusHours}
                usedHours={entitlementUsed}
              />
            </Box>

            <Box sx={{ mt: 1 }}>
              <StatDivider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25 }}>
                <Typography sx={{ fontSize: 14, color: sub.greyMuted }}>Used</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.colors.textPrimary }}>
                  {usedHoursDisplay}
                </Typography>
              </Box>
              <StatDivider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25 }}>
                <Typography sx={{ fontSize: 14, color: sub.greyMuted }}>Remaining</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.colors.textPrimary }}>
                  {entitlementRemaining != null ? formatHoursLabel(entitlementRemaining) : remainingHoursDisplay}
                </Typography>
              </Box>
              {sessionsAllowance != null ? (
                <>
                  <StatDivider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25 }}>
                    <Typography sx={{ fontSize: 14, color: sub.greyMuted }}>Sessions this month</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.colors.textPrimary }}>
                      {currentMonthSessions != null ? Math.round(currentMonthSessions) : 0} / {Math.round(sessionsAllowance)}
                    </Typography>
                  </Box>
                </>
              ) : null}
              <StatDivider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25 }}>
                <Typography sx={{ fontSize: 14, color: sub.greyMuted }}>Next billing</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.colors.textPrimary }}>{planCardNextBilling}</Typography>
              </Box>
            </Box>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={7}>
          <SectionCard sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary, maxWidth: 360 }}>
                Monthly UV usage vs plan ceiling
              </Typography>
              <Typography sx={{ fontSize: 12, color: sub.greyMuted, fontWeight: 500 }}>
                Avg {avgH.toFixed(1)} h · peak {peakH.toFixed(1)} h{peakMonth && peakH > 0 ? ` (${peakMonth})` : ''}
              </Typography>
            </Box>
            {usageChartIsPlaceholder ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 260,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.colors.textPrimary }}>
                  No disinfection usage recorded yet
                </Typography>
                <Typography sx={{ fontSize: 12, color: sub.greyMuted, maxWidth: 320 }}>
                  Monthly UV hours will appear here once this client&apos;s robots start logging sessions.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 260 }}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={usageChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: sub.greyMuted }} axisLine={{ stroke: '#e4e4e7' }} tickLine={false} />
                    <YAxis
                      domain={[0, chartYMax]}
                      ticks={Array.from({ length: chartYMax / 10 + 1 }, (_, i) => i * 10)}
                      tickFormatter={(v) => `${v}h`}
                      tick={{ fontSize: 12, fill: sub.greyMuted }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: `${sub.purple}08` }}
                      contentStyle={{ borderRadius: 8, border: `1px solid ${theme.colors.borderDefault}` }}
                    />
                    <ReferenceLine
                      y={tierCeiling}
                      stroke={sub.ceiling}
                      strokeDasharray="6 4"
                      strokeWidth={2}
                      label={{
                        value: 'Plan ceiling',
                        position: 'insideTopRight',
                        fill: sub.ceiling,
                        fontSize: 11,
                      }}
                    />
                    <Bar dataKey="hours" fill={sub.purple} radius={[6, 6, 0, 0]} name="Hours" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}

            <DashboardInsightCard
              title="Usage forecast"
              insightsFromView={usageForecastInsight}
              source={usageForecastSource}
              loading={loading}
              disclaimerMode="short"
              attributionLive="Generated by AI from your current subscription and usage data."
              attributionCached="Generated by AI from your subscription and usage data."
              emptyBodyText="Updating with AI"
              adminCacheInvalidation={{
                surface: 'subscription',
                cacheParams: {
                  email: subEmail,
                  calcMonth: currentYYYYMM(),
                },
                onInvalidated: refetchUsageForecast,
              }}
              sx={{ mt: 1, mb: 0 }}
            />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Lifetime KPI strip */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[
          {
            label: 'LIFETIME HOURS',
            value: lifetimeHoursVal != null ? `${Number(lifetimeHoursVal).toFixed(1)} h` : '—',
            sub: lifetimeHoursSub || '—',
          },
          {
            label: 'USER TOTAL SESSIONS',
            value: totalSessionsVal != null ? String(Math.round(totalSessionsVal)) : '—',
            sub: totalSessionsSub || '—',
          },
          {
            label: 'CURRENT MONTH HOURS',
            value: currentMonthHoursVal != null ? `${Number(currentMonthHoursVal).toFixed(1)} h` : '—',
            sub: currentMonthHoursSub || '—',
          },
          {
            label: 'CURRENT MONTH SESSIONS',
            value: currentMonthSessionsVal != null ? String(Math.round(currentMonthSessionsVal)) : '—',
            sub: currentMonthSessionsSub || '—',
          },
        ].map((k) => (
          <Grid item xs={12} sm={6} md={3} key={k.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '12px',
                bgcolor: '#fff',
                border: `1px solid ${theme.colors.borderDefault}`,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                height: '100%',
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: sub.greyMuted, mb: 1 }}>{k.label}</Typography>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: theme.colors.textPrimary, lineHeight: 1.1 }}>{k.value}</Typography>
              <Typography sx={{ fontSize: 13, color: sub.greyMuted, mt: 0.75, fontWeight: 500 }}>{k.sub}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Lifecycle timeline */}
      {lifecycle ? (
        <Box sx={{ mb: 2.5 }}>
          <SectionCard title="Subscription lifecycle" subtitle="Trial through paid commitment, next charge and expiry">
            <LifecycleTimeline lifecycle={lifecycle} />
          </SectionCard>
        </Box>
      ) : null}

      {/* Billing ledger */}
      <Box sx={{ mb: 2.5 }}>
        <SectionCard
          title="Billing history"
          subtitle="Every payment, refund and authorisation on this account"
        >
          <BillingLedger rows={paymentRows} />
        </SectionCard>
      </Box>

    </Box>
  );
}
